import axios, { type AxiosError } from 'axios';

import { ApiError, type SdkApiErrorBody, type SdkUtils } from './types';

function apiErrorStatus(error: unknown): number | null {
  if (error instanceof ApiError) {
    return error.status;
  }

  if (axios.isAxiosError(error)) {
    return error.response?.status ?? 0;
  }

  return null;
}

/** Normalizes an error from `Sdk.api` (ApiError) or an Axios call (AxiosError). */
export function extractApiErrorBody(error: unknown): SdkApiErrorBody | null {
  if (error instanceof ApiError) {
    return isApiErrorBody(error.body) ? error.body : null;
  }

  if (axios.isAxiosError(error)) {
    return isApiErrorBody(error.response?.data) ? error.response.data : null;
  }

  return null;
}

/** The server's message, or `fallback` when the body has none or only an empty one, as a bare `abort(403)` sends. */
export function apiErrorMessage(error: unknown, fallback = 'Request failed.'): string {
  const message = extractApiErrorBody(error)?.message;

  return typeof message === 'string' && message.trim() !== '' ? message : fallback;
}

export function isValidationError(error: unknown): boolean {
  return apiErrorStatus(error) === 422;
}

export function isConflict(error: unknown): boolean {
  return apiErrorStatus(error) === 409;
}

export function isForbidden(error: unknown): boolean {
  return apiErrorStatus(error) === 403;
}

export function isNotFound(error: unknown): boolean {
  return apiErrorStatus(error) === 404;
}

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    return new ApiError(
      axiosError.response?.status ?? 0,
      axiosError.response?.data ?? null,
      axiosError.message
    );
  }

  if (error instanceof Error) {
    return new ApiError(0, null, error.message);
  }

  return new ApiError(0, null, 'Unknown request error');
}

function isApiErrorBody(body: unknown): body is SdkApiErrorBody {
  return typeof body === 'object' && body !== null && !Array.isArray(body);
}

export const apiErrorUtils: SdkUtils['error'] = {
  extractApiErrorBody,
  apiErrorMessage,
  isValidationError,
  isConflict,
  isForbidden,
  isNotFound
};
